import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  canTransitionMeeting,
  isTerminalMeetingStatus,
  MEETING_MODE_LABELS,
  MEETING_MODES,
  MEETING_STATUS_MEANINGS,
  type MeetingMode,
  type MeetingParty,
} from "@titleverify/shared";
import { ApiError, apiErrorText, apiFetch, type Meeting, type MeetingList } from "../api.js";
import { useAuth } from "../auth.js";
import { LoadFailure } from "./LoadFailure.js";

/**
 * MEETINGS, BETWEEN THE TWO PEOPLE ON THE CASE AND NOBODY ELSE.
 * ===========================================================================
 *
 * READ apps/api/src/meetings/service.test.ts BEFORE CHANGING THIS FILE. The
 * describe block "a meeting exists only between the case's two participants" is
 * the compliance gate this surface exists under, and it refuses, in order: a
 * meeting on a case with no assigned attorney, an ADMIN ("the platform does not
 * broker meetings"), a stranger, and an unrelated attorney. Every shaping
 * decision below follows from it:
 *
 *  - THERE IS NO ATTORNEY PICKER, no "find someone to meet", no availability
 *    calendar, no list of professionals, and no name, photograph, rating or
 *    contact detail of any professional anywhere in this flow. The other party
 *    is addressed as "the attorney on your case" / "the client" and that is the
 *    whole of what is ever said about them. Rendering a professional's identity
 *    to help someone choose IS the touting surface COMPLIANCE Constraint 1 (and
 *    Supreme Court Rule 39) exists to prevent.
 *  - IT IS NOT MOUNTED FOR AN ADMIN. `meeting:participate` grants `client`
 *    (isOwnCase) and `professional` (isAssignedProfessional) and nothing else —
 *    an admin is refused with ForbiddenError by an explicitly commented rule.
 *    So there is no ops "arrange a meeting" control here, and there must not be:
 *    a meeting the platform brokered would imply it is soliciting on the
 *    attorney's behalf. `search_agent` is not granted either and has no surface.
 *  - NO PRICE, NO FEE, NO "CONSULTATION COST". D1 is open, the meetings API has
 *    no money field, and this must not grow one.
 *
 * WHAT THE BROWSER CANNOT KNOW, AND THEREFORE DOES NOT GUESS. The server decides
 * "may this person accept?" on the requester's USER ID (service.ts assertRecipient)
 * — deliberately, because a role comparison breaks under reassignment. That user
 * id is NOT on the wire. So this file cannot compute whether a request has been
 * stranded by a reassignment, and it does not try: the Accept control is rendered,
 * the server refuses, and its sentence is shown verbatim. Hiding a control is a
 * courtesy; the server is what refuses.
 */

type Props = {
  caseId: string;
  /** The viewer's party on this case. There are only ever two. */
  viewerParty: MeetingParty;
  /**
   * Straight off the case payload. Used ONLY to decide whether asking the server
   * for meetings is worth the round trip — the same column the service reads, so
   * there is no second rule here to drift out of step with the first.
   */
  assignedProfessionalId: string | null;
};

export function CaseMeetings({ caseId, viewerParty, assignedProfessionalId }: Props) {
  const { getToken } = useAuth();
  const isAssigned = assignedProfessionalId !== null;

  const meetings = useQuery({
    queryKey: ["meetings", caseId],
    queryFn: async () =>
      apiFetch<MeetingList>(`/cases/${caseId}/meetings`, { token: await getToken() }),
    /*
     * Not asked at all until the case has an attorney. The service answers an
     * unassigned case with a 400 whose message is the honest explanation, and
     * that message is rendered below from `NothingToArrangeYet` either way — so
     * this is purely about not firing a request whose answer we would render
     * identically. If the server refuses for that reason anyway, the error
     * branch catches it and shows the same panel.
     */
    enabled: isAssigned,
    retry: false,
  });

  return (
    <section className="stack">
      <h3>Meeting the attorney on this case</h3>

      {!isAssigned ? <NothingToArrangeYet viewerParty={viewerParty} /> : null}

      {isAssigned && meetings.isPending ? (
        <p className="muted" role="status">Loading the meetings on this case…</p>
      ) : null}

      {meetings.error ? <MeetingsError error={meetings.error} viewerParty={viewerParty} /> : null}

      {meetings.data && meetings.data.meetings.length === 0 ? (
        <p className="empty">
          Nothing has been proposed on this case yet. That is the normal starting point,
          not a problem — a meeting only exists once one of you asks for one.
        </p>
      ) : null}

      {meetings.data && meetings.data.meetings.length > 0 ? (
        /*
         * `.case-list` is the design system's list reset (list-style:none, no
         * padding) and is used here for exactly that. Each meeting is a generic
         * `.card` rather than a `.case-card`, because a meeting is not a case.
         */
        <ul className="case-list">
          {meetings.data.meetings.map((meeting) => (
            <li key={meeting.id}>
              <MeetingCard meeting={meeting} viewerParty={viewerParty} />
            </li>
          ))}
        </ul>
      ) : null}

      {isAssigned && !meetings.error ? (
        <ProposeMeeting caseId={caseId} viewerParty={viewerParty} />
      ) : null}
    </section>
  );
}

/**
 * A case with no attorney on it. A `.notice`, NEVER `[role="alert"]`.
 *
 * This is a legitimate, expected early state for a SUBMITTED or ROUTED case, not
 * a failure — and the proposal form is absent rather than present-and-broken.
 * The copy also says what the platform will NOT do next, because the obvious
 * reading of "nobody is assigned yet" is "then find me somebody", and that is
 * precisely the request this platform cannot take.
 */
function NothingToArrangeYet({ viewerParty }: { viewerParty: MeetingParty }) {
  return (
    <div className="notice">
      <h3>There is nobody on this case to meet yet</h3>
      <p>
        A meeting can only be arranged once an attorney is assigned to the case, and this
        one has no attorney yet. Nothing has gone wrong; that is simply where the case is.
      </p>
      <p>
        {viewerParty === "client" ? (
          <>
            A meeting here is only ever between you and the attorney-at-law already assigned
            to your case. <strong>The platform does not arrange one for you</strong> and will
            not introduce you to anyone — where a case goes is decided by registry
            jurisdiction and verified standing, and there is no directory of professionals
            here to pick from.
          </>
        ) : (
          <>
            A meeting here is only ever between a client and the attorney-at-law already
            assigned to their case. Until this case is assigned there is nothing to arrange
            from either side.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * The list failed. One case gets its own panel: the 400 that means "not assigned
 * yet" is a true fact worth knowing, not a problem, and must not be dressed as
 * an alert just because it arrived as a non-2xx.
 *
 * THIS IS NOT DEAD CODE BEHIND THE `enabled` GUARD. A revocation nulls
 * `assigned_professional_id` through the cascade in migration 0016 §7, so a case
 * payload already in hand can say "assigned" while the server now sees null. The
 * query fires, the service answers 400, and the reader gets the honest panel
 * rather than a red alert about a case that simply lost its attorney.
 *
 * Matched on the status rather than the message text: the only ValidationError
 * `listCaseMeetings` can raise is that one, and matching prose would break the
 * moment somebody rewords it.
 */
function MeetingsError({ error, viewerParty }: { error: unknown; viewerParty: MeetingParty }) {
  if (error instanceof ApiError && error.status === 400 && error.fromApi) {
    return <NothingToArrangeYet viewerParty={viewerParty} />;
  }
  return (
    <LoadFailure
      error={error}
      fallback="Could not load the meetings on this case."
      format={meetingErrorText}
    >
      <p>
        <small>
          Any meeting already proposed or agreed on this case still stands. It is the list
          that did not load.
        </small>
      </p>
    </LoadFailure>
  );
}

/**
 * ONE MEETING.
 *
 * WHICH CONTROLS ARE DRAWN comes from `canTransitionMeeting` in
 * @titleverify/shared — the same table the service asserts against and the DB
 * trigger mirrors — so the edge list is never restated here. A declined or
 * cancelled meeting is terminal and shows no controls at all: it does not come
 * back to life, and re-proposing means a NEW meeting.
 *
 * "DID I PROPOSE THIS?" IS COSMETIC AND NOTHING MORE. It is derived from
 * `requestedBy`, which is a PARTY label, and the server deliberately does not
 * decide it that way — it uses the requester's user id, because a client
 * accepting a request from an attorney who has since been reassigned away would
 * otherwise bind the NEW attorney to a meeting they never proposed. The
 * inference holds for a client (a case has one client) and can be wrong for a
 * professional. It decides what is worth drawing and decides nothing else.
 */
function MeetingCard({ meeting, viewerParty }: { meeting: Meeting; viewerParty: MeetingParty }) {
  const iProposed = meeting.requestedBy === viewerParty;
  const terminal = isTerminalMeetingStatus(meeting.status);

  const mayAccept = canTransitionMeeting(meeting.status, "scheduled") && !iProposed;
  const mayDecline = canTransitionMeeting(meeting.status, "declined") && !iProposed;
  const mayCancel = canTransitionMeeting(meeting.status, "cancelled");
  const mayComplete = canTransitionMeeting(meeting.status, "completed");

  const [open, setOpen] = useState<null | "accept" | "decline" | "cancel" | "complete">(null);

  /*
   * FOUR BUTTONS, ONE DISCLOSURE REGION. They are mutually exclusive toggles into
   * the same area below, so they all point `aria-controls` at that one region and
   * at most one ever reports `aria-expanded="true"`. The region element is always
   * rendered while the meeting is live, even with nothing open — `aria-controls`
   * naming an id that is not in the document resolves to nothing, which is the
   * silent failure this was fixing.
   */
  const panelId = `meeting-${meeting.id}-panel`;
  const toggle = (key: "accept" | "decline" | "cancel" | "complete") =>
    setOpen((current) => (current === key ? null : key));

  // Withdrawing your own unanswered request and cancelling an arranged meeting are
  // the same transition, so the label — open and closed — follows which one it is.
  const withdrawing = iProposed && meeting.status === "requested";
  const cancelLabel = withdrawing ? "Withdraw this request" : "Cancel";
  const cancelOpenLabel = withdrawing
    ? "Hide the withdrawal confirmation"
    : "Hide the cancellation confirmation";

  return (
    <article className="card">
      <header className="card-head">
        <h4 className="card-title">{MEETING_MODE_LABELS[meeting.mode]}</h4>
        {/*
          A meeting status is WORKFLOW STATE, so `.state` — monospace and
          deliberately neutral. The `.status-*` palette is a judgement about a
          PERSON's verification standing; colouring "declined" red here would
          invent a verdict, and someone declining a proposed time is not a bad
          actor.
        */}
        <p className="card-meta">
          <span className="state">{meeting.status}</span>
        </p>
      </header>

      <p className="muted">{MEETING_STATUS_MEANINGS[meeting.status]}</p>

      <dl>
        <dt>Time</dt>
        <dd>
          {meeting.scheduledAt === null
            ? "No time has been agreed."
            : formatWhen(meeting.scheduledAt)}
          {meeting.scheduledAt !== null && meeting.status === "requested" ? (
            <>
              {" "}
              <span className="muted">— proposed, not yet agreed.</span>
            </>
          ) : null}
        </dd>

        {/*
          Only the coordinate the mode actually uses is shown. Both columns can
          hold a value, but a location on an online meeting is not a fact about
          anything — the server checks the mode-appropriate one and ignores the
          other.
        */}
        {meeting.mode === "online" ? (
          <>
            <dt>Joining link</dt>
            <dd>{meeting.meetingLink ?? "No joining link has been given yet."}</dd>
          </>
        ) : (
          <>
            <dt>Where</dt>
            <dd>{meeting.location ?? "No location has been given yet."}</dd>
          </>
        )}

        <dt>Proposed</dt>
        <dd>
          {formatWhen(meeting.createdAt)} — {proposerLine(viewerParty, meeting.requestedBy)}
        </dd>
      </dl>

      {terminal ? null : (
        <>
          <div className="actions">
            {/*
              Each label says which of the two things pressing it will now do,
              because that is what a sighted reader sees: the panel below is
              either showing or it is not. `aria-expanded` carries the same fact
              to a reader who cannot see it appear. The "Hide …" wording mirrors
              the Show/Hide documents disclosure on the professional's case list,
              so the two behave alike.
            */}
            {mayAccept ? (
              <button
                type="button"
                aria-expanded={open === "accept"}
                aria-controls={panelId}
                onClick={() => toggle("accept")}
              >
                {open === "accept" ? "Hide the acceptance form" : "Accept and set a time"}
              </button>
            ) : null}
            {mayDecline ? (
              <button
                type="button"
                className="quiet"
                aria-expanded={open === "decline"}
                aria-controls={panelId}
                onClick={() => toggle("decline")}
              >
                {open === "decline" ? "Hide the decline confirmation" : "Decline"}
              </button>
            ) : null}
            {mayComplete ? (
              <button
                type="button"
                className="quiet"
                aria-expanded={open === "complete"}
                aria-controls={panelId}
                onClick={() => toggle("complete")}
              >
                {open === "complete" ? "Hide the completion confirmation" : "Mark as happened"}
              </button>
            ) : null}
            {mayCancel ? (
              <button
                type="button"
                className="danger"
                aria-expanded={open === "cancel"}
                aria-controls={panelId}
                onClick={() => toggle("cancel")}
              >
                {open === "cancel" ? cancelOpenLabel : cancelLabel}
              </button>
            ) : null}
          </div>

          {/*
            The region the four buttons control. Rendered whenever any of them
            is, empty or not, so `aria-controls` always resolves to a real
            element. `aria-live` is deliberately absent: the disclosure is the
            user's own act, and announcing it twice is noise.
          */}
          <div id={panelId}>
            {open === "accept" ? (
              <AcceptMeeting meeting={meeting} onDone={() => setOpen(null)} />
            ) : null}
            {open === "decline" ? (
              <SimpleAction meeting={meeting} verb="decline" onDone={() => setOpen(null)} />
            ) : null}
            {open === "cancel" ? (
              <SimpleAction
                meeting={meeting}
                verb="cancel"
                iProposed={iProposed}
                onDone={() => setOpen(null)}
              />
            ) : null}
            {open === "complete" ? (
              <SimpleAction meeting={meeting} verb="complete" onDone={() => setOpen(null)} />
            ) : null}
          </div>
        </>
      )}
    </article>
  );
}

/**
 * PROPOSING A MEETING. The same form for both participants — no role branching,
 * because either may propose and the server stamps who did.
 *
 * `requestedBy` IS NOT A FIELD HERE. The server derives it from the actor's
 * resolved party and stamps `requestedByUserId` from their user id; neither can
 * be sent, and offering either would imply a control that does not exist.
 */
function ProposeMeeting({ caseId, viewerParty }: { caseId: string; viewerParty: MeetingParty }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<MeetingMode>("online");
  const [proposedAt, setProposedAt] = useState("");
  const [location, setLocation] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [tooSoon, setTooSoon] = useState(false);

  const propose = useMutation({
    mutationFn: async () => {
      /*
       * Only keys with a real value are sent. Two reasons, both concrete:
       * `location: ""` would be STORED as an empty string rather than left null,
       * and `meetingLink: ""` fails `z.string().url()` and comes back as a zod
       * error whose per-field detail this app does not read.
       *
       * Only the coordinate matching the chosen mode is sent, so switching the
       * radio after typing cannot post a location on an online meeting.
       */
      const body: Record<string, unknown> = { mode };
      if (proposedAt !== "") body.proposedAt = new Date(proposedAt).toISOString();
      if (mode === "physical" && location.trim() !== "") body.location = location.trim();
      if (mode === "online" && meetingLink.trim() !== "") body.meetingLink = meetingLink.trim();

      return apiFetch<Meeting>(`/cases/${caseId}/meetings`, {
        token: await getToken(),
        method: "POST",
        body,
      });
    },
    onSuccess: () => {
      setProposedAt("");
      setLocation("");
      setMeetingLink("");
      void queryClient.invalidateQueries({ queryKey: ["meetings", caseId] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    /*
     * A COURTESY CHECK OVER A REAL DATABASE RULE, and framed as one.
     *
     * The trigger `meeting_proposal_is_in_the_future` (migration 0017 §5) refuses
     * an insert whose scheduled_at is not in the future. Nothing between the form
     * and that trigger checks it — not the zod schema, not the service — so the
     * Postgres check_violation reaches the generic handler and the user gets a
     * bare 500 with no reason at all. This check is the only way they learn what
     * is actually wrong. It enforces nothing: the trigger is the rule, and if one
     * slips past this the 500 is still rendered honestly below.
     */
    if (proposedAt !== "" && new Date(proposedAt).getTime() <= Date.now()) {
      setTooSoon(true);
      return;
    }
    setTooSoon(false);
    propose.mutate();
  }

  return (
    <form className="stack" onSubmit={submit}>
      <div className="consequence">
        <h3>Proposing a meeting</h3>
        {/*
          NOT "sends". `requestMeeting` inserts a row and writes an audit entry;
          that is the whole of it. There is no notifier in apps/api/src/meetings,
          and the consented `notificationChannels` on intake are about delivering
          the report, not this. Saying a request was SENT would have the reader
          waiting on an answer nobody has been told to give — a fact the platform
          did not earn (non-negotiable 1). So this says what the act does: it
          records something the other party finds when they next look.
        */}
        <p>
          This records a proposal on the case for{" "}
          {viewerParty === "client"
            ? "the attorney-at-law assigned to your case"
            : "the client on this case"}{" "}
          to answer. <strong>They are not told about it.</strong> The one message this
          platform sends is an email when an attorney's opinion is ready; a meeting proposal
          goes on no channel at all — so they will see it whenever they next
          open this case, and this screen has no way of telling you whether they have. If it
          is time-sensitive, do not rely on this record alone to reach them.
        </p>
        <p>
          It is a proposal, not an appointment:{" "}
          <strong>nothing is agreed until the other party accepts it</strong>, and they may
          decline. A time is optional — a request with no time is a perfectly good &ldquo;can
          we meet?&rdquo;
        </p>
        <p>
          Whatever you enter as a location or a joining link is shown to the other
          participant and is kept on the case record.{" "}
          <strong>
            Unlike an uploaded document, it carries no deletion date — nothing on this
            platform removes it on a schedule.
          </strong>{" "}
          Enter only what is needed to find the meeting.
        </p>
      </div>

      <fieldset>
        <legend>How would you like to meet?</legend>
        {/* Rendered from MEETING_MODES so the form cannot offer a mode the server
            would refuse, and a mode added to the shared constant is not silently
            missing here. */}
        {MEETING_MODES.map((option) => (
          <p key={option}>
            <label>
              <input
                type="radio"
                name="meeting-mode"
                value={option}
                checked={mode === option}
                onChange={() => setMode(option)}
              />{" "}
              {MEETING_MODE_LABELS[option]}
            </label>
          </p>
        ))}
      </fieldset>

      <div className="field">
        <label htmlFor="proposed-at">A time you would suggest</label>
        <input
          id="proposed-at"
          type="datetime-local"
          min={localNowForInput()}
          value={proposedAt}
          onChange={(event) => setProposedAt(event.target.value)}
        />
        <small className="field-hint">
          Optional, and it must be in the future. Leave it blank to ask for a meeting
          without suggesting when — the other party can set the time when they accept.
        </small>
      </div>

      {mode === "physical" ? (
        <div className="field">
          <label htmlFor="propose-location">Where you suggest meeting</label>
          <input
            id="propose-location"
            type="text"
            maxLength={500}
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
          <small className="field-hint">
            Optional here — whoever accepts must supply one before an in-person meeting
            can be scheduled. Shown to the other participant and kept on the case record,
            with no deletion date on it.
          </small>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="propose-link">A joining link</label>
          <input
            id="propose-link"
            type="url"
            maxLength={500}
            value={meetingLink}
            onChange={(event) => setMeetingLink(event.target.value)}
          />
          <small className="field-hint">
            Optional here — whoever accepts must supply one before an online meeting can be
            scheduled. It must be a full web address. Shown to the other participant and
            kept on the case record, with no deletion date on it.
          </small>
        </div>
      )}

      {tooSoon ? (
        <p role="alert">
          That time has already passed. A proposal is about the future, so pick a time from
          now onwards.
        </p>
      ) : null}

      {propose.error ? (
        <p role="alert">{meetingErrorText(propose.error, "Could not record this proposal.")}</p>
      ) : null}

      <div className="actions">
        <button type="submit" disabled={propose.isPending}>
          {propose.isPending ? "Recording…" : "Record this proposal"}
        </button>
      </div>
    </form>
  );
}

/**
 * ACCEPTING. Only ever drawn on a meeting the viewer did not propose — and that
 * is a courtesy, not the rule. The server refuses a self-accept, and refuses a
 * request whose proposer has left the case, with sentences rendered verbatim
 * below because they tell the reader something this screen could not work out.
 *
 * WHICH FIELDS ARE REQUIRED DEPENDS ON THE ROW, not on the mode alone. The
 * service merges the submitted value with what is already on the meeting, so if
 * the proposer supplied the coordinate the accepter may omit it; if they did
 * not, it is mandatory. That is why this reads `meeting.location` /
 * `meeting.meetingLink` before deciding what to ask for.
 */
function AcceptMeeting({ meeting, onDone }: { meeting: Meeting; onDone: () => void }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const needsLink = meeting.mode === "online" && !meeting.meetingLink?.trim();
  const needsLocation = meeting.mode === "physical" && !meeting.location?.trim();

  const [scheduledAt, setScheduledAt] = useState(toInputValue(meeting.scheduledAt));
  const [location, setLocation] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [tooSoon, setTooSoon] = useState(false);

  const accept = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        scheduledAt: new Date(scheduledAt).toISOString(),
      };
      if (location.trim() !== "") body.location = location.trim();
      if (meetingLink.trim() !== "") body.meetingLink = meetingLink.trim();

      return apiFetch<Meeting>(`/meetings/${meeting.id}/accept`, {
        token: await getToken(),
        method: "POST",
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meetings", meeting.caseId] });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    /*
     * THE SERVER DOES NOT ENFORCE THIS ONE, AND THIS CHECK IS NOT A SUBSTITUTE.
     *
     * The future-time trigger is BEFORE INSERT only; migration 0015 leaves
     * scheduled_at mutable and acceptSchema has no future check, so an accept
     * carrying a past time is written and the meeting is recorded as scheduled
     * for a moment that has already gone. That is a server-side hole — a
     * coordination row asserting a time no participant could have agreed to is
     * the shape non-negotiable 1 forbids — and it is raised rather than papered
     * over here. This check stops the honest mistake; it does not close the hole,
     * and no copy on this screen claims the service validated the time.
     */
    if (new Date(scheduledAt).getTime() <= Date.now()) {
      setTooSoon(true);
      return;
    }
    setTooSoon(false);
    accept.mutate();
  }

  return (
    <form className="stack" onSubmit={submit}>
      <div className="consequence">
        <h3>Accepting this request</h3>
        <p>
          Accepting <strong>schedules the meeting for both of you</strong> at the time you
          set below. It is not a further proposal — the other party is not asked again.
        </p>
        <p>
          A scheduled meeting can still be cancelled by either of you, but it cannot be
          declined afterwards and it cannot be turned back into a request.
        </p>
        {needsLink || needsLocation ? (
          <p>
            {needsLink
              ? "This is an online meeting and no joining link has been given yet, so one is needed now."
              : "This is an in-person meeting and no location has been given yet, so one is needed now."}{" "}
            It is shown to the other participant and kept on the case record, with no
            deletion date on it.
          </p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor={`accept-at-${meeting.id}`}>When it will happen</label>
        <input
          id={`accept-at-${meeting.id}`}
          type="datetime-local"
          min={localNowForInput()}
          value={scheduledAt}
          onChange={(event) => setScheduledAt(event.target.value)}
          required
        />
        <small className="field-hint">
          {meeting.scheduledAt === null
            ? "No time was suggested, so this one is yours to set."
            : "Pre-filled with the time that was proposed. Change it if it does not suit."}
        </small>
      </div>

      {needsLocation ? (
        <div className="field">
          <label htmlFor={`accept-location-${meeting.id}`}>Where it will happen</label>
          <input
            id={`accept-location-${meeting.id}`}
            type="text"
            maxLength={500}
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            required
          />
          <small className="field-hint">
            Needed before an in-person meeting can be scheduled.
          </small>
        </div>
      ) : null}

      {needsLink ? (
        <div className="field">
          <label htmlFor={`accept-link-${meeting.id}`}>The joining link</label>
          <input
            id={`accept-link-${meeting.id}`}
            type="url"
            maxLength={500}
            value={meetingLink}
            onChange={(event) => setMeetingLink(event.target.value)}
            required
          />
          <small className="field-hint">
            Needed before an online meeting can be scheduled. It must be a full web address.
          </small>
        </div>
      ) : null}

      {tooSoon ? (
        <p role="alert">
          That time has already passed. Set a time from now onwards, so the meeting record
          says something both of you could have agreed to.
        </p>
      ) : null}

      {accept.error ? (
        /*
         * The server's own sentence, verbatim. On this endpoint the 400 messages
         * ARE the explanation and are things this screen could not have worked
         * out for itself — above all "The participant who proposed this meeting
         * is no longer on the case; it must be proposed again", which depends on
         * a user id that never crosses the wire.
         */
        <p role="alert">{meetingErrorText(accept.error, "Could not accept this request.")}</p>
      ) : null}

      <div className="actions">
        <button type="submit" disabled={accept.isPending}>
          {accept.isPending ? "Accepting…" : "Confirm and schedule it"}
        </button>
        <button type="button" className="quiet" onClick={onDone}>
          Leave it as it is
        </button>
      </div>
    </form>
  );
}

/**
 * Decline, cancel and complete: no body, no fields, one confirmation each.
 *
 * NO "WHY?" TEXTAREA anywhere here. None of these endpoints accepts a reason and
 * none stores one — the server would discard it, and a box that quietly throws
 * away what someone wrote is worse than no box. The audit log records a reason
 * CODE, which is an internal artefact and never shown.
 *
 * Each opens a `.consequence` first, because all three are terminal and the
 * server will not undo any of them.
 */
function SimpleAction({
  meeting,
  verb,
  iProposed = false,
  onDone,
}: {
  meeting: Meeting;
  verb: "decline" | "cancel" | "complete";
  iProposed?: boolean;
  onDone: () => void;
}) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const act = useMutation({
    mutationFn: async () =>
      apiFetch<Meeting>(`/meetings/${meeting.id}/${verb}`, {
        token: await getToken(),
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meetings", meeting.caseId] });
      onDone();
    },
  });

  const copy = CONSEQUENCE[verb];

  return (
    <div className="stack">
      <div className="consequence">
        <h3>{copy.heading}</h3>
        <p>{copy.body}</p>
        <p>
          <strong>This cannot be undone.</strong> {copy.terminal}
        </p>
        {verb === "cancel" && iProposed && meeting.status === "requested" ? (
          <p>
            This is your own request, which nobody has answered yet. Withdrawing it is the
            right way to take it back — you cannot decline your own request.
          </p>
        ) : null}
      </div>

      {act.error ? (
        <p role="alert">{meetingErrorText(act.error, `Could not ${verb} this meeting.`)}</p>
      ) : null}

      <div className="actions">
        <button
          type="button"
          className={verb === "complete" ? undefined : "danger"}
          disabled={act.isPending}
          onClick={() => act.mutate()}
        >
          {act.isPending ? "Working…" : copy.confirm}
        </button>
        <button type="button" className="quiet" onClick={onDone}>
          Leave it as it is
        </button>
      </div>
    </div>
  );
}

const CONSEQUENCE: Record<
  "decline" | "cancel" | "complete",
  { heading: string; body: string; terminal: string; confirm: string }
> = {
  decline: {
    heading: "Declining this request",
    // Not "tells": nothing is transmitted here either. The status changes on the
    // record and the other participant reads it when they next open the case.
    body: "This records that you are not taking up this request. No time is set and no meeting is arranged, and the other participant sees the refusal when they next open this case — nothing notifies them of it.",
    terminal:
      "A declined request is closed for good — it cannot be reopened or accepted later. If you do want to meet at some other time, one of you proposes a new meeting; this one stays on the case record as declined.",
    confirm: "Decline this request",
  },
  cancel: {
    heading: "Calling this meeting off",
    body: "This closes the meeting for both participants. If a time was set, it no longer stands.",
    terminal:
      "A cancelled meeting is closed for good and cannot be reinstated. Meeting after all means proposing a new one; this one stays on the case record as cancelled.",
    confirm: "Cancel this meeting",
  },
  complete: {
    heading: "Marking this meeting as having happened",
    body: "This records that the meeting took place. Only mark it if it actually did — the record is what the platform holds about this case, and it should not say something happened that did not.",
    terminal:
      "A completed meeting is final: it cannot be reopened, rescheduled or cancelled afterwards.",
    confirm: "It happened",
  },
};

/**
 * Who proposed it, said no more precisely than the wire allows.
 *
 * The payload carries a PARTY, not a person. For a client the inference is
 * sound — a case has exactly one client. For a professional it is not: a case
 * can change hands, and a request labelled "professional" may have come from an
 * attorney who has since been reassigned away. So that one line says what it
 * does not know rather than asserting the likely answer, and no line here ever
 * names anybody.
 */
function proposerLine(viewerParty: MeetingParty, requestedBy: MeetingParty): string {
  if (viewerParty === "client") {
    return requestedBy === "client"
      ? "you proposed this."
      : "proposed by an attorney on this case.";
  }
  return requestedBy === "professional"
    ? "proposed from the attorney's side of this case. If the case has changed hands since, that may have been a different attorney — the record here does not say which."
    : "the client proposed this.";
}

/**
 * Turns a failure into a sentence without claiming more than the response
 * licensed. Two shapes on this surface need handling that `apiErrorText` cannot
 * give them, and both would otherwise put something useless or untrue on screen.
 */
function meetingErrorText(error: unknown, whatFailed: string): string {
  if (error instanceof ApiError && error.fromApi) {
    /*
     * A zod failure answers { error: "invalid_request", issues: [...] } with NO
     * top-level `message`, so ApiError.message falls back to the bare token
     * "invalid_request" and the per-field detail is dropped by apiFetch. Printing
     * that token at a person is printing a variable name.
     */
    if (error.status === 400 && error.message === "invalid_request") {
      return (
        `${whatFailed} The service rejected the details as malformed, and answered with a ` +
        `field-by-field list this screen does not read — so it cannot tell you which field ` +
        `it meant. Check the time and any web address you entered.`
      );
    }
    /*
     * The future-time rule lives ONLY in a database trigger, and nothing catches
     * the check_violation, so a past proposed time arrives as a bare 500 with no
     * reason in it. Naming that as THE cause would be stating a fact the response
     * did not give us — so it is named as a known possibility and explicitly not
     * as a finding.
     */
    if (error.status === 500) {
      return (
        `${whatFailed} The service reported an internal error rather than a reason, so this ` +
        `screen cannot tell you what it objected to. One known cause is a proposed time that ` +
        `is not in the future — but nothing in the response says that is what happened here, ` +
        `and this page will not claim it did.`
      );
    }
  }
  return apiErrorText(error, whatFailed);
}

/** Date AND time — a meeting at "20/07/2026" with no hour is not a coordination fact. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/**
 * `now`, in the shape a datetime-local input wants (local time, no zone, to the
 * minute). Used as `min` — a courtesy that keeps the picker from offering a past
 * time, never the thing that refuses one.
 */
function localNowForInput(): string {
  return toInputValue(new Date().toISOString());
}

/**
 * ISO string → the `YYYY-MM-DDTHH:mm` a datetime-local input requires, in LOCAL
 * time. `toISOString().slice(0,16)` is the tempting one-liner and it is wrong:
 * it yields UTC, so every prefilled time would be shown shifted by the reader's
 * offset — 5½ hours out in Sri Lanka.
 */
function toInputValue(iso: string | null): string {
  if (iso === null) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}
